/**
 * Centralized localStorage service with SSR-safe guards, type safety, and error handling.
 *
 * Eliminates scattered localStorage boilerplate across the codebase.
 *
 * @example
 * ```typescript
 * import { storage } from "@/common/services/localStorage.service"
 *
 * // Save filters
 * storage.set("my-tools-filters", { searchQuery: "", filter: "all" })
 *
 * // Load with fallback
 * const filters = storage.get("my-tools-filters", { searchQuery: "", filter: "all" })
 *
 * // Load with Zod validation
 * const validated = storage.getValidated("my-tools-filters", FiltersSchema, defaultFilters)
 *
 * // Remove item
 * storage.remove("my-tools-filters")
 *
 * // Check existence
 * if (storage.has("my-tools-filters")) { ... }
 * ```
 */

import type { z } from "zod"

import { STORAGE_KEYS } from "./localStorage.keys"

/**
 * SSR-safe guard - returns true if localStorage is available
 *
 * NOTE: `typeof window.localStorage` is NOT safe — it triggers the property getter,
 * which throws SecurityError in cross-origin iframes (e.g. embed pages) before
 * the typeof operator can intercept it. Must use try/catch.
 */
const isClient = (): boolean => {
  if (typeof window === "undefined") return false
  try {
    return window.localStorage !== undefined
  } catch {
    return false
  }
}

/**
 * Get item from localStorage with type safety and optional fallback
 *
 * @param key - Storage key
 * @param fallback - Optional fallback value if key doesn't exist or parsing fails
 * @returns Parsed value or fallback
 */
/* eslint-disable no-redeclare -- TypeScript overload signatures share one implementation */
function get<T>(key: string): T | undefined
function get<T>(key: string, fallback: T): T
function get<T>(key: string, fallback?: T): T | undefined {
  if (!isClient()) return fallback

  let item: string | null = null
  try {
    item = localStorage.getItem(key)
    if (item === null) return fallback

    return JSON.parse(item) as T
  } catch (error) {
    console.error(`[localStorage] Failed to parse "${key}":`, error)
    console.error(`[localStorage] Corrupted value:`, item)
    console.error(`[localStorage] Removing corrupted key...`)
    // Clean up corrupted data
    try {
      localStorage.removeItem(key)
    } catch {
      // removeItem can throw in locked-down contexts (e.g. cross-origin iframe)
    }
    return fallback
  }
}
/* eslint-enable no-redeclare */

/**
 * Get item from localStorage with Zod schema validation
 *
 * @param key - Storage key
 * @param schema - Zod schema for validation
 * @param fallback - Fallback value if validation fails
 * @returns Validated value or fallback
 */
function getValidated<T>(key: string, schema: z.ZodSchema<T>, fallback: T): T {
  if (!isClient()) return fallback

  try {
    const item = localStorage.getItem(key)
    if (item === null) return fallback

    const parsed = JSON.parse(item)
    const validated = schema.safeParse(parsed)

    return validated.success ? validated.data : fallback
  } catch (error) {
    console.error(`[localStorage] Failed to get and validate "${key}":`, error)
    return fallback
  }
}

/**
 * Set item in localStorage with JSON serialization
 *
 * @param key - Storage key
 * @param value - Value to store (will be JSON.stringify'd)
 */
function set<T>(key: string, value: T): void {
  if (!isClient()) return

  try {
    localStorage.setItem(key, JSON.stringify(value))
  } catch (error) {
    console.error(`[localStorage] Failed to set "${key}":`, error)
    // Possible causes: QuotaExceededError, TypeError, SecurityError
  }
}

/**
 * Remove item from localStorage
 *
 * @param key - Storage key to remove
 */
function remove(key: string): void {
  if (!isClient()) return

  try {
    localStorage.removeItem(key)
  } catch (error) {
    console.error(`[localStorage] Failed to remove "${key}":`, error)
  }
}

/**
 * Check if key exists in localStorage
 *
 * @param key - Storage key to check
 * @returns True if key exists, false otherwise
 */
function has(key: string): boolean {
  if (!isClient()) return false

  try {
    return localStorage.getItem(key) !== null
  } catch (error) {
    console.error(`[localStorage] Failed to check "${key}":`, error)
    return false
  }
}

/**
 * Clear all items from localStorage
 *
 * WARNING: This clears EVERYTHING, use with caution!
 */
function clear(): void {
  if (!isClient()) return

  try {
    localStorage.clear()
  } catch (error) {
    console.error("[localStorage] Failed to clear:", error)
  }
}

/**
 * Get all keys from localStorage
 *
 * @returns Array of all storage keys
 */
function getAllKeys(): string[] {
  if (!isClient()) return []

  try {
    return Object.keys(localStorage)
  } catch (error) {
    console.error("[localStorage] Failed to get keys:", error)
    return []
  }
}

/**
 * Centralized localStorage service
 */
export const storage = {
  get,
  getValidated,
  set,
  remove,
  has,
  clear,
  getAllKeys,
  keys: STORAGE_KEYS
}
